import { ref, computed, watch, onUnmounted, type Ref, type ComputedRef } from 'vue';
import { decodeGif, type ParsedGif } from '@/utils/gifDecoder';

export interface GifPlayer {
  frameCount: ComputedRef<number>;
  currentFrame: Ref<number>;
  isPlaying: Ref<boolean>;
  isLoading: Ref<boolean>;
  play(): void;
  pause(): void;
  toggle(): void;
  seekTo(frame: number): void;
}

/**
 * Fetches and decodes a GIF, then drives frame-by-frame playback onto a canvas element.
 *
 * @param src     Reactive source URL (blob: or http:). Changing it reloads the GIF.
 * @param canvas  Ref to the canvas element where frames will be drawn.
 */
export function useGifPlayer(
  src: Ref<string>,
  canvas: Ref<HTMLCanvasElement | null>,
): GifPlayer {
  const gif = ref<ParsedGif | null>(null);
  const currentFrame = ref(0);
  const isPlaying = ref(false);
  const isLoading = ref(false);
  let timer: ReturnType<typeof setTimeout> | null = null;

  const frameCount = computed(() => gif.value?.frames.length ?? 0);

  function clearTimer() {
    if (timer !== null) { clearTimeout(timer); timer = null; }
  }

  function renderFrame(index: number) {
    const g = gif.value;
    const el = canvas.value;
    if (!g || !el || index >= g.frames.length) return;
    const ctx = el.getContext('2d');
    if (!ctx) return;
    ctx.putImageData(new ImageData(g.frames[index].pixels, g.width, g.height), 0, 0);
  }

  function scheduleNext() {
    clearTimer();
    if (!isPlaying.value || !gif.value) return;
    const delay = gif.value.frames[currentFrame.value]?.delay ?? 100;
    timer = setTimeout(() => {
      if (!isPlaying.value || !gif.value) return;
      currentFrame.value = (currentFrame.value + 1) % gif.value.frames.length;
      renderFrame(currentFrame.value);
      scheduleNext();
    }, delay);
  }

  function applyCanvasDimensions(el: HTMLCanvasElement) {
    const g = gif.value;
    if (!g) return;
    el.width = g.width;
    el.height = g.height;
  }

  // When the canvas becomes available after decoding, size it and render.
  watch(canvas, (el) => {
    if (el && gif.value) { applyCanvasDimensions(el); renderFrame(currentFrame.value); }
  });

  watch(src, async (newSrc) => {
    clearTimer();
    gif.value = null;
    currentFrame.value = 0;
    isPlaying.value = false;
    if (!newSrc) return;

    isLoading.value = true;
    try {
      const resp = await fetch(newSrc);
      const buf = await resp.arrayBuffer();
      gif.value = decodeGif(buf);
      if (gif.value.frames.length > 0) {
        const el = canvas.value;
        if (el) applyCanvasDimensions(el);
        renderFrame(0);
        isPlaying.value = true;
        scheduleNext();
      }
    } catch {
      // failed to load or decode — stay in empty state
    } finally {
      isLoading.value = false;
    }
  }, { immediate: true });

  function play() {
    if (isPlaying.value || !gif.value) return;
    isPlaying.value = true;
    scheduleNext();
  }

  function pause() {
    isPlaying.value = false;
    clearTimer();
  }

  function toggle() {
    if (isPlaying.value) pause(); else play();
  }

  function seekTo(frame: number) {
    if (!gif.value) return;
    const n = Math.max(0, Math.min(Math.round(frame), gif.value.frames.length - 1));
    currentFrame.value = n;
    renderFrame(n);
    if (isPlaying.value) {
      clearTimer();
      scheduleNext();
    }
  }

  onUnmounted(pause);

  return {
 frameCount,
currentFrame,
isPlaying,
isLoading,
play,
pause,
toggle,
seekTo,
};
}
